import { useState, useEffect } from 'react';
import HomeButton from './shared/HomeButton';
import AdminSidebarHeader from './shared/AdminSidebarHeader';
import SidebarActionBand from './shared/SidebarActionBand';
import SidebarFooter from './shared/SidebarFooter';
import AddNewModal from './shared/AddNewModal';
import SidebarSearchFilter from './shared/SidebarSearchFilter';
import { adminApi } from '../lib/admin-api';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { useAuth } from '../contexts/AuthContext';
import CreateProductModal from './admin/CreateProductModal';
import CreateInvitationModal from './admin/CreateInvitationModal';
import CreateRoleModal from './admin/CreateRoleModal';
import DeleteProductModal from './admin/DeleteProductModal';
import DeleteConfirmationModal from './shared/DeleteConfirmationModal';
import SidebarListItem from './shared/SidebarListItem';
import Badge from './shared/Badge';
import ContextMenu from './shared/ContextMenu';
import NotificationBanners from './shared/NotificationBanners';
import toast from 'react-hot-toast';

export default function AdminSidebar({ activeTab, isCollapsed = false, onActionButtonClick, selectedConfigId, onConfigSelect, aiOverview, modelConfigs, isLoadingOverview, onRefreshOverview, users, selectedUserId, onUserSelect, invitations = [], selectedInvitationId, onInvitationSelect, onRefreshInvitations, products, selectedProductId, onProductSelect, onProductDelete, isLoadingProducts, roles, selectedRoleId, onRoleSelect, isLoadingRoles, permissions, selectedPermissionId, onPermissionSelect, isLoadingPermissions }) {
  // Get current user
  const { user: currentUser } = useAuth();

  // Unified delete hooks
  // Note: We don't manually clear selection here - the useAdminSection hook
  // automatically detects when selected items are missing from refreshed lists
  // and clears the selection, preventing race conditions and 404 errors.
  const { handleDelete: deleteUser } = useAdminDelete({
    resourceType: 'user',
    getItemName: (user) => user?.full_name || user?.username || 'User',
    skipConfirmation: true,
    onSuccess: async () => {
      await onRefreshOverview?.();
    }
  });

  const { handleDelete: deleteRole } = useAdminDelete({
    resourceType: 'role',
    getItemName: (role) => role?.name || 'Role',
    skipConfirmation: true,
    onSuccess: async () => {
      await onRefreshOverview?.();
    }
  });

  const { handleDelete: deleteConfiguration, deleteModal: configDeleteModal } = useAdminDelete({
    resourceType: 'configuration',
    getItemName: (config) => config?.name || 'Configuration',
    onSuccess: async () => {
      await onRefreshOverview?.();
    }
  });

  const { handleDelete: deleteProduct } = useAdminDelete({
    resourceType: 'product',
    getItemName: (product) => product?.name || 'Product',
    onSuccess: async () => {
      await onRefreshOverview?.();
    }
  });

  // Navigation helpers for collapsed mode
  const getNavigationInfo = () => {
    if (activeTab === 'ai') {
      const configs = modelConfigs || [];
      const currentIndex = configs.findIndex(config => config.id === selectedConfigId);
      const currentItem = configs[currentIndex];
      const canGoPrev = currentIndex > 0;
      const canGoNext = currentIndex < configs.length - 1;

      return {
        currentTitle: currentItem?.name || 'No Selection',
        onPrevious: canGoPrev ? () => onConfigSelect?.(configs[currentIndex - 1].id) : null,
        onNext: canGoNext ? () => onConfigSelect?.(configs[currentIndex + 1].id) : null
      };
    } else if (activeTab === 'users') {
      const currentIndex = users.findIndex(user => user.id === selectedUserId);
      const currentItem = users[currentIndex];
      const canGoPrev = currentIndex > 0;
      const canGoNext = currentIndex < users.length - 1;

      return {
        currentTitle: currentItem?.fullName || currentItem?.username || 'No Selection',
        onPrevious: canGoPrev ? () => onUserSelect?.(users[currentIndex - 1].id) : null,
        onNext: canGoNext ? () => onUserSelect?.(users[currentIndex + 1].id) : null
      };
    } else if (activeTab === 'products') {
      const currentIndex = products.findIndex(product => product.id === selectedProductId);
      const currentItem = products[currentIndex];
      const canGoPrev = currentIndex > 0;
      const canGoNext = currentIndex < products.length - 1;

      return {
        currentTitle: currentItem?.name || 'No Selection',
        onPrevious: canGoPrev ? () => onProductSelect?.(products[currentIndex - 1].id) : null,
        onNext: canGoNext ? () => onProductSelect?.(products[currentIndex + 1].id) : null
      };
    } else if (activeTab === 'roles') {
      const currentIndex = roles?.findIndex(role => role.id === selectedRoleId) ?? -1;
      const currentItem = roles?.[currentIndex];
      const canGoPrev = currentIndex > 0;
      const canGoNext = currentIndex < (roles?.length || 0) - 1;

      return {
        currentTitle: currentItem?.name || 'No Selection',
        onPrevious: canGoPrev ? () => onRoleSelect?.(roles[currentIndex - 1].id) : null,
        onNext: canGoNext ? () => onRoleSelect?.(roles[currentIndex + 1].id) : null
      };
    } else if (activeTab === 'permissions') {
      const currentIndex = permissions?.findIndex(perm => perm.id === selectedPermissionId) ?? -1;
      const currentItem = permissions?.[currentIndex];
      const canGoPrev = currentIndex > 0;
      const canGoNext = currentIndex < (permissions?.length || 0) - 1;

      return {
        currentTitle: currentItem?.display_name || 'No Selection',
        onPrevious: canGoPrev ? () => onPermissionSelect?.(permissions[currentIndex - 1].id) : null,
        onNext: canGoNext ? () => onPermissionSelect?.(permissions[currentIndex + 1].id) : null
      };
    }

    return {
      currentTitle: 'No Selection',
      onPrevious: null,
      onNext: null
    };
  };

  const navigationInfo = getNavigationInfo();

  // Section metadata based on active tab
  const sectionInfo = {
    users: {
      title: 'Users',
      icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
      actionButtons: []
    },
    products: {
      title: 'Products',
      icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
      actionButtons: []
    },
    roles: {
      title: 'Roles',
      icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
      actionButtons: []
    },
    permissions: {
      title: 'Permissions',
      icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
      actionButtons: []
    },
    integrations: {
      title: 'Integrations Management',
      icon: 'M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z',
      actionButtons: []
    },
    ai: {
      title: 'Configurations',
      icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
      actionButtons: [
        {
          id: 'parameters',
          label: 'Parameters',
          icon: 'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4',
          color: 'blue'
        },
        {
          id: 'connections',
          label: 'Connections',
          icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
          color: 'green'
        },
        {
          id: 'models',
          label: 'Models',
          icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
          color: 'purple'
        }
      ]
    }
  };

  const currentSection = sectionInfo[activeTab] || sectionInfo.users;

  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [showCreateInvitationModal, setShowCreateInvitationModal] = useState(false);
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteProductModal, setShowDeleteProductModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);

  // Search and filter state
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userActiveFilter, setUserActiveFilter] = useState('all');
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productActiveFilter, setProductActiveFilter] = useState('all');
  const [roleSearchQuery, setRoleSearchQuery] = useState('');

  // Use modelConfigs from prop (includes themes)
  // Fallback to empty array if not provided
  const configs = modelConfigs || [];

  // Auto-select first config if none selected
  useEffect(() => {
    if (activeTab === 'ai' && modelConfigs && modelConfigs.length > 0 && !selectedConfigId) {
      onConfigSelect?.(modelConfigs[0].id);
    }
  }, [activeTab, modelConfigs, selectedConfigId, onConfigSelect]);

  // Auto-select first user if none selected (unless an invitation is selected)
  useEffect(() => {
    if (activeTab === 'users' && users && users.length > 0 && !selectedUserId && !selectedInvitationId) {
      onUserSelect?.(users[0].id);
    }
  }, [activeTab, users, selectedUserId, selectedInvitationId, onUserSelect]);

  // Auto-select first product if none selected (only on initial load, not during deletion)
  useEffect(() => {
    // Only auto-select if we're on products tab, have products, no selection, AND products just loaded
    // Don't auto-select if selection was just cleared (like during deletion)
    if (activeTab === 'products' && products && products.length > 0 && selectedProductId === undefined) {
      onProductSelect?.(products[0].id);
    }
  }, [activeTab, products, selectedProductId, onProductSelect]);

  // Auto-select first role if none selected
  useEffect(() => {
    if (activeTab === 'roles' && roles && roles.length > 0 && !selectedRoleId) {
      onRoleSelect?.(roles[0].id);
    }
  }, [activeTab, roles, selectedRoleId, onRoleSelect]);

  const handleActionClick = (buttonId) => {
    onActionButtonClick?.(buttonId);
  };

  const handleAddClick = async () => {
    if (activeTab === 'ai') {
      // Directly create a new configuration with auto-generated name
      try {
        const result = await adminApi.modelConfigurations.create({});
        // Select the newly created configuration first
        onConfigSelect?.(result.id);
        // Then refresh overview data to show the new model configuration
        await onRefreshOverview?.();
      } catch (error) {
        console.error('Failed to create model configuration:', error);
        alert('Failed to create model configuration');
      }
    } else if (activeTab === 'products') {
      setShowCreateProductModal(true);
    } else if (activeTab === 'users') {
      setShowCreateInvitationModal(true);
    } else if (activeTab === 'roles') {
      setShowCreateRoleModal(true);
    } else {
      setShowAddModal(true);
    }
  };

  // Context menu handlers for users
  const handleUserContextMenu = (e, user) => {
    setContextMenu({ x: e.clientX, y: e.clientY, type: 'user', data: user });
  };

  const handleUserActivateToggle = async (user) => {
    try {
      await adminApi.users.update(user.id, { is_active: !user.is_active });
      toast.success(`User ${!user.is_active ? 'activated' : 'deactivated'} successfully`);
      onRefreshOverview?.();
    } catch (error) {
      console.error('Failed to toggle user active status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleUserDelete = async (user) => {
    await deleteUser(user);
  };

  // Context menu handlers for products
  const handleProductContextMenu = (e, product) => {
    setContextMenu({ x: e.clientX, y: e.clientY, type: 'product', data: product });
  };

  const handleProductActivateToggle = async (product) => {
    try {
      await adminApi.products.update(product.id, { is_active: !product.is_active });
      toast.success(`Product ${!product.is_active ? 'activated' : 'deactivated'} successfully`);
      onRefreshOverview?.();
    } catch (error) {
      console.error('Failed to toggle product active status:', error);
      toast.error('Failed to update product status');
    }
  };

  const handleProductDelete = (product) => {
    // Open the custom delete modal instead of confirm()
    setProductToDelete(product);
    setShowDeleteProductModal(true);
  };

  const confirmProductDelete = async () => {
    if (!productToDelete) return;

    // Use unified delete hook with skipConfirm since modal already confirmed
    await deleteProduct(productToDelete, { skipConfirm: true });
  };

  // Context menu handlers for AI configurations
  const handleConfigContextMenu = (e, config) => {
    setContextMenu({ x: e.clientX, y: e.clientY, type: 'config', data: config });
  };

  const handleConfigActivateToggle = async (config) => {
    try {
      await adminApi.modelConfigurations.update(config.id, {
        is_active: !config.is_active
      });
      toast.success(`Configuration ${!config.is_active ? 'activated' : 'deactivated'} successfully`);
      await onRefreshOverview?.();
    } catch (error) {
      console.error('Failed to toggle configuration active status:', error);
      toast.error('Failed to update configuration status');
    }
  };

  const handleConfigDelete = async (config) => {
    await deleteConfiguration(config);
  };

  // Context menu handlers for roles
  const handleRoleContextMenu = (e, role) => {
    setContextMenu({ x: e.clientX, y: e.clientY, type: 'role', data: role });
  };

  const handleRoleDelete = async (role) => {
    await deleteRole(role);
  };

  // Build context menu options based on type
  const getContextMenuOptions = () => {
    if (!contextMenu) return [];

    const { type, data } = contextMenu;

    if (type === 'user') {
      const menuItems = [];

      // Check if we should show the activate/deactivate option
      const canToggleActive = !(
        (data.is_organization_creator && data.is_active) || // Can't deactivate org creator
        (data.username === currentUser?.username)           // Can't deactivate yourself
      );

      // Check if we should show the delete option
      const canDelete = !(
        data.is_organization_creator ||                     // Can't delete org creator
        (data.username === currentUser?.username)           // Can't delete yourself
      );

      if (canToggleActive) {
        menuItems.push({
          label: data.is_active ? 'Deactivate' : 'Activate',
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={data.is_active ? "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"} />
            </svg>
          ),
          onClick: () => handleUserActivateToggle(data)
        });
      }

      if (canDelete) {
        if (canToggleActive) {
          menuItems.push({ divider: true });
        }
        menuItems.push({
          label: 'Delete',
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          ),
          onClick: () => handleUserDelete(data),
          danger: true
        });
      }

      return menuItems;
    } else if (type === 'product') {
      return [
        {
          label: data.is_active ? 'Deactivate' : 'Activate',
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={data.is_active ? "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"} />
            </svg>
          ),
          onClick: () => handleProductActivateToggle(data)
        },
        { divider: true },
        {
          label: 'Delete',
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          ),
          onClick: () => handleProductDelete(data),
          danger: true
        }
      ];
    } else if (type === 'config') {
      return [
        {
          label: data.is_active ? 'Deactivate' : 'Activate',
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={data.is_active ? "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"} />
            </svg>
          ),
          onClick: () => handleConfigActivateToggle(data)
        },
        { divider: true },
        {
          label: 'Delete',
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          ),
          onClick: () => handleConfigDelete(data),
          danger: true
        }
      ];
    } else if (type === 'role') {
      return [
        {
          label: 'Delete',
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          ),
          onClick: () => handleRoleDelete(data),
          danger: true
        }
      ];
    }

    return [];
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Admin Header - Same height as page headers (h-16 = 64px) */}
      <div className="h-16 border-b flex items-center px-4 flex-shrink-0" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border-default)' }}>
        {!isCollapsed ? (
          <div className="flex items-center w-full">
            {/* Left: Home Button */}
            <div className="flex-shrink-0">
              <HomeButton />
            </div>

            {/* Admin Title */}
            <div className="flex-1 flex items-center ml-4">
              <h2 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Admin</h2>
            </div>
          </div>
        ) : (
          // Collapsed: Show only home button
          <div className="w-full flex justify-center">
            <HomeButton />
          </div>
        )}
      </div>

      {/* Section Title Band */}
      <AdminSidebarHeader
        icon={currentSection.icon}
        title={currentSection.title}
        showAddButton={['users', 'products', 'ai', 'roles'].includes(activeTab)}
        onAddClick={handleAddClick}
        addButtonTooltip={
          activeTab === 'ai' ? 'Create new model configuration' :
          activeTab === 'users' ? 'Add new user' :
          activeTab === 'products' ? 'Add new product' :
          activeTab === 'roles' ? 'Create new role' :
          'Add new'
        }
        isCollapsed={isCollapsed}
        // Navigation props for collapsed mode
        collapsedTitle={navigationInfo.currentTitle}
        onPrevious={navigationInfo.onPrevious}
        onNext={navigationInfo.onNext}
      />

      {/* Notification Banners */}
      <NotificationBanners onCreateProduct={() => setShowCreateProductModal(true)} />

      {/* User List (only for users tab) */}
      {activeTab === 'users' && (
        <div className="flex-1 overflow-y-auto flex flex-col">
          <SidebarSearchFilter
            searchQuery={userSearchQuery}
            onSearchChange={setUserSearchQuery}
            showActiveFilter={true}
            activeFilter={userActiveFilter}
            onActiveFilterChange={setUserActiveFilter}
            placeholder="Search users..."
          />
          <div className="flex-1 overflow-y-auto p-4">
            {!users ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full border-b-2 border-purple-500 h-8 w-8"></div>
              </div>
            ) : (() => {
              // Apply filters
              const filteredUsers = users.filter(user => {
                // Active filter
                if (userActiveFilter === 'active' && !user.is_active) return false;
                if (userActiveFilter === 'inactive' && user.is_active) return false;

                // Search filter
                if (userSearchQuery) {
                  const query = userSearchQuery.toLowerCase();
                  const matchesUsername = user.username?.toLowerCase().includes(query);
                  const matchesFullName = user.full_name?.toLowerCase().includes(query);
                  const matchesEmail = user.email?.toLowerCase().includes(query);
                  return matchesUsername || matchesFullName || matchesEmail;
                }

                return true;
              });

              return (
                <div className="space-y-6">
                {/* Active Users Section */}
                {(() => {
                  const activeUsers = filteredUsers.filter(user => user.is_active);
                if (activeUsers.length > 0) {
                  return (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                        Active Users
                      </h3>
                      <div className="space-y-2">
                        {activeUsers.map((user) => {
                          const isSelected = selectedUserId === user.id;
                          const displayName = user.full_name || user.username;

                          return (
                            <SidebarListItem
                              key={user.id}
                              isSelected={isSelected}
                              onClick={() => onUserSelect?.(user.id)}
                              onContextMenu={(e) => handleUserContextMenu(e, user)}
                              title={displayName}
                              isActive={true}
                              statusBadge={
                                <Badge variant="gray">
                                  active
                                </Badge>
                              }
                              badges={
                                <div
                                  className="flex gap-2"
                                  title={user.roles && user.roles.length > 0
                                    ? user.roles.map(r => r.name).join(', ')
                                    : user.role || ''}
                                >
                                  {user.roles && user.roles.length > 0
                                    ? user.roles.slice(0, 2).map((role, idx) => (
                                        <Badge key={idx} variant="gray">
                                          {role.name}
                                        </Badge>
                                      ))
                                    : user.role
                                    ? (
                                        <Badge variant="gray">
                                          {user.role}
                                        </Badge>
                                      )
                                    : null}
                                </div>
                              }
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Disabled Users Section */}
              {(() => {
                const disabledUsers = filteredUsers.filter(user => !user.is_active);
                if (disabledUsers.length > 0) {
                  return (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                        Disabled Users
                      </h3>
                      <div className="space-y-2">
                        {disabledUsers.map((user) => {
                          const isSelected = selectedUserId === user.id;
                          const displayName = user.full_name || user.username;

                          return (
                            <SidebarListItem
                              key={user.id}
                              isSelected={isSelected}
                              onClick={() => onUserSelect?.(user.id)}
                              onContextMenu={(e) => handleUserContextMenu(e, user)}
                              title={displayName}
                              isActive={false}
                              statusBadge={
                                <Badge variant="gray">
                                  inactive
                                </Badge>
                              }
                              badges={
                                <div
                                  className="flex gap-2"
                                  title={user.roles && user.roles.length > 0
                                    ? user.roles.map(r => r.name).join(', ')
                                    : user.role || ''}
                                >
                                  {user.roles && user.roles.length > 0
                                    ? user.roles.slice(0, 2).map((role, idx) => (
                                        <Badge key={idx} variant="gray">
                                          {role.name}
                                        </Badge>
                                      ))
                                    : user.role
                                    ? (
                                        <Badge variant="gray">
                                          {user.role}
                                        </Badge>
                                      )
                                    : null}
                                </div>
                              }
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Invited Users Section */}
              {(() => {
                if (invitations.length > 0) {
                  return (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                        Invited Users
                      </h3>
                      <div className="space-y-2">
                        {invitations.map((invitation) => {
                          const isSelected = selectedInvitationId === invitation.id;
                          const expiresDate = new Date(invitation.expires_at).toLocaleDateString();

                          return (
                            <SidebarListItem
                              key={invitation.id}
                              isSelected={isSelected}
                              onClick={() => onInvitationSelect?.(invitation.id)}
                              title="Pending Invite"
                              isActive={true}
                              badges={[
                                <Badge key="expires" variant="gray">
                                  expires: {expiresDate}
                                </Badge>
                              ]}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Product List (only for products tab) */}
      {activeTab === 'products' && (
        <div className="flex-1 overflow-y-auto flex flex-col">
          <SidebarSearchFilter
            searchQuery={productSearchQuery}
            onSearchChange={setProductSearchQuery}
            showActiveFilter={true}
            activeFilter={productActiveFilter}
            onActiveFilterChange={setProductActiveFilter}
            placeholder="Search products..."
          />
          <div className="flex-1 overflow-y-auto p-4">
            {isLoadingProducts ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full border-b-2 border-purple-500 h-8 w-8"></div>
              </div>
            ) : (
              <div className="space-y-2">
                {products && products.filter(product => {
                  // Active filter
                  if (productActiveFilter === 'active' && !product.is_active) return false;
                  if (productActiveFilter === 'inactive' && product.is_active) return false;

                  // Search filter
                  if (productSearchQuery) {
                    const query = productSearchQuery.toLowerCase();
                    const matchesName = product.name?.toLowerCase().includes(query);
                    const matchesCategory = product.category?.toLowerCase().includes(query);
                    const matchesVersion = product.version?.toLowerCase().includes(query);
                    return matchesName || matchesCategory || matchesVersion;
                  }

                  return true;
                }).map((product) => {
                  const isSelected = selectedProductId === product.id;
                  const isActive = product.is_active;
                  const productName = product.name || 'Unknown Product';

                return (
                  <SidebarListItem
                    key={product.id}
                    isSelected={isSelected}
                    onClick={() => onProductSelect?.(product.id)}
                    onContextMenu={(e) => handleProductContextMenu(e, product)}
                    title={productName}
                    isActive={isActive}
                    statusBadge={
                      <Badge variant="gray">
                        {isActive ? 'active' : 'inactive'}
                      </Badge>
                    }
                    badges={[
                      <Badge key="version" variant="gray">
                        {product.version || 'no version'}
                      </Badge>,
                      <span key="category" className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>{product.category}</span>
                    ]}
                  />
                );
              })}
            </div>
            )}
          </div>
        </div>
      )}

      {/* Role List (only for roles tab) */}
      {activeTab === 'roles' && (
        <div className="flex-1 overflow-y-auto flex flex-col">
          <SidebarSearchFilter
            searchQuery={roleSearchQuery}
            onSearchChange={setRoleSearchQuery}
            showActiveFilter={false}
            placeholder="Search roles..."
          />
          <div className="flex-1 overflow-y-auto p-4">
          {isLoadingRoles ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full border-b-2 border-purple-500 h-8 w-8"></div>
            </div>
          ) : (
            <div className="space-y-2">
              {roles && roles.filter(role => {
                // Search filter
                if (roleSearchQuery) {
                  const query = roleSearchQuery.toLowerCase();
                  const matchesName = role.name?.toLowerCase().includes(query);
                  const matchesTheme = role.theme?.toLowerCase().includes(query);
                  return matchesName || matchesTheme;
                }

                return true;
              }).map((role) => {
                const isSelected = selectedRoleId === role.id;
                const userCount = role.user_count || 0;
                const permissionCount = role.permission_ids?.length || 0;

                return (
                  <SidebarListItem
                    key={role.id}
                    isSelected={isSelected}
                    onClick={() => onRoleSelect?.(role.id)}
                    onContextMenu={(e) => handleRoleContextMenu(e, role)}
                    title={role.name}
                    isActive={true}
                    badges={[
                      <Badge key="users" variant="gray">
                        {userCount} {userCount === 1 ? 'user' : 'users'}
                      </Badge>,
                      <Badge key="permissions" variant="gray">
                        {permissionCount} {permissionCount === 1 ? 'permission' : 'permissions'}
                      </Badge>
                    ]}
                  />
                );
              })}
            </div>
          )}
          </div>
        </div>
      )}

      {/* Permission List (only for permissions tab) */}
      {activeTab === 'permissions' && (
        <div className="flex-1 overflow-y-auto p-4">
          {isLoadingPermissions ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full border-b-2 border-purple-500 h-8 w-8"></div>
            </div>
          ) : (
            <div className="space-y-2">
              {permissions && permissions.map((permission) => {
                const isSelected = selectedPermissionId === permission.id;
                const permissionName = permission.display_name || permission.name;
                const userCount = permission.user_count || 0;
                const roleCount = permission.role_count || 0;

                return (
                  <SidebarListItem
                    key={permission.id}
                    isSelected={isSelected}
                    onClick={() => onPermissionSelect?.(permission.id)}
                    title={permissionName}
                    isActive={true}
                    badges={[
                      <Badge key="users" variant="gray">
                        {userCount} {userCount === 1 ? 'user' : 'users'}
                      </Badge>,
                      <Badge key="roles" variant="gray">
                        {roleCount} {roleCount === 1 ? 'role' : 'roles'}
                      </Badge>
                    ]}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* AI Model List (only for AI tab) */}
      {activeTab === 'ai' && (
        <div className="flex-1 overflow-y-auto p-4">
          {isLoadingOverview ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full border-b-2 border-blue-500 h-8 w-8"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Active Configurations Section */}
              {(() => {
                const activeConfigs = configs.filter(config => config.is_active);
                if (activeConfigs.length > 0) {
                  return (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                        Active Configurations
                      </h3>
                      <div className="space-y-2">
                        {activeConfigs.map((config) => {
                          const isSelected = selectedConfigId === config.id;
                          const isActive = config.is_active;
                          const jobName = String(config.job || 'unknown');

                          return (
                            <SidebarListItem
                              key={config.id}
                              isSelected={isSelected}
                              onClick={() => onConfigSelect?.(config.id)}
                              onContextMenu={(e) => handleConfigContextMenu(e, config)}
                              title={config.name}
                              isActive={isActive}
                              badges={[
                                <Badge key="job" variant="gray">
                                  {jobName}
                                </Badge>,
                                config.model_name && (
                                  <Badge key="model" variant="gray">
                                    {config.model_name}
                                  </Badge>
                                )
                              ].filter(Boolean)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Other Configurations Section */}
              {(() => {
                const inactiveConfigs = configs.filter(config => !config.is_active);
                if (inactiveConfigs.length > 0) {
                  return (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                        Other Configurations
                      </h3>
                      <div className="space-y-2">
                        {inactiveConfigs.map((config) => {
                          const isSelected = selectedConfigId === config.id;
                          const isActive = config.is_active;
                          const jobName = String(config.job || 'unknown');

                          return (
                            <SidebarListItem
                              key={config.id}
                              isSelected={isSelected}
                              onClick={() => onConfigSelect?.(config.id)}
                              onContextMenu={(e) => handleConfigContextMenu(e, config)}
                              title={config.name}
                              isActive={isActive}
                              badges={[
                                <Badge key="job" variant="gray">
                                  {jobName}
                                </Badge>,
                                config.model_name && (
                                  <Badge key="model" variant="gray">
                                    {config.model_name}
                                  </Badge>
                                )
                              ].filter(Boolean)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </div>
      )}

      {/* Empty Space for other tabs */}
      {activeTab !== 'ai' && activeTab !== 'users' && activeTab !== 'products' && activeTab !== 'roles' && activeTab !== 'permissions' && (
        <div className="flex-1 overflow-y-auto"></div>
      )}

      {/* Action Buttons - Moved to bottom above footer */}
      {currentSection.actionButtons.length > 0 && (
        <div className="p-4 border-t border-gray-700 flex-shrink-0 flex gap-3">
          {currentSection.actionButtons.map((button) => {
            const colorClasses = {
              green: 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 border-green-500/30 shadow-green-500/20',
              red: 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 border-red-500/30 shadow-red-500/20',
              blue: 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 border-blue-500/30 shadow-blue-500/20',
              purple: 'bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 border-purple-500/30 shadow-purple-500/20'
            };

            return (
              <button
                key={button.id}
                onClick={() => handleActionClick(button.id)}
                title={button.label}
                className={`flex-1 ${colorClasses[button.color]} rounded-xl font-semibold transition-all duration-200 flex items-center justify-center border shadow-lg hover:shadow-xl hover:scale-105 p-3`}
                style={{ color: 'var(--color-text-on-accent)' }}
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={button.icon} />
                </svg>
              </button>
            );
          })}
        </div>
      )}

      {/* Action Band - Add New Button */}
      <SidebarActionBand
        show={['users', 'products', 'ai'].includes(activeTab)}
        label={
          activeTab === 'ai' ? 'New Configuration' :
          activeTab === 'users' ? 'Invite User' :
          activeTab === 'products' ? 'Add New Product' :
          'Add New'
        }
        icon={
          activeTab === 'users'
            ? 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z' // User add icon
            : 'M12 4v16m8-8H4' // Plus icon
        }
        onClick={handleAddClick}
        isCollapsed={isCollapsed}
      />

      {/* Footer Info */}
      <SidebarFooter
        primaryText="Admin Access Required"
        secondaryText="v1.0.0"
        isCollapsed={isCollapsed}
      />

      {/* Create Product Modal */}
      {showCreateProductModal && (
        <CreateProductModal
          isOpen={showCreateProductModal}
          onClose={() => setShowCreateProductModal(false)}
          onProductCreated={(newProduct) => {
            setShowCreateProductModal(false);
            // Trigger a product list refresh by calling the parent's refresh handler
            // The Admin page should provide this callback
            window.location.reload(); // Temporary solution - ideally use a refresh callback
          }}
        />
      )}

      {/* Create Invitation Modal */}
      {showCreateInvitationModal && (
        <CreateInvitationModal
          isOpen={showCreateInvitationModal}
          onClose={() => setShowCreateInvitationModal(false)}
          onSuccess={(invitation) => {
            // Invitation created successfully - refresh invitations list
            if (onRefreshInvitations) {
              onRefreshInvitations();
            }
          }}
        />
      )}

      {/* Create Role Modal */}
      {showCreateRoleModal && (
        <CreateRoleModal
          isOpen={showCreateRoleModal}
          onClose={() => setShowCreateRoleModal(false)}
          onRoleCreated={(newRole) => {
            setShowCreateRoleModal(false);
            // Refresh roles list
            if (onRefreshOverview) {
              onRefreshOverview();
            }
            // Select the newly created role
            if (onRoleSelect && newRole?.id) {
              onRoleSelect(newRole.id);
            }
          }}
        />
      )}

      {/* Add New Modal for Users/Products */}
      <AddNewModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={async (data) => {
          console.log('Creating new item:', data);
          // TODO: Implement create API calls
          alert(`Creating new ${activeTab.slice(0, -1)}: ${JSON.stringify(data, null, 2)}`);
          setShowAddModal(false);
        }}
        title={activeTab === 'users' ? 'Invite' : activeTab === 'products' ? 'Add New Product' : 'Add New'}
        submitLabel="Create"
        fields={
          activeTab === 'users' ? [
            {
              name: 'username',
              label: 'Username',
              type: 'text',
              placeholder: 'Enter username',
              required: true,
              validate: (value) => {
                if (value && value.length < 3) return 'Username must be at least 3 characters';
                return null;
              }
            },
            {
              name: 'fullName',
              label: 'Full Name',
              type: 'text',
              placeholder: 'Enter full name',
              required: true
            },
            {
              name: 'email',
              label: 'Email',
              type: 'email',
              placeholder: 'Enter email address',
              required: true,
              validate: (value) => {
                if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                  return 'Please enter a valid email address';
                }
                return null;
              }
            },
            {
              name: 'role',
              label: 'Role',
              type: 'select',
              placeholder: 'Select role',
              required: true,
              options: [
                { value: 'user', label: 'User' },
                { value: 'admin', label: 'Admin' }
              ]
            },
            {
              name: 'is_active',
              label: 'Active',
              type: 'checkbox',
              defaultValue: true,
              description: 'User account is active and can log in'
            }
          ] : activeTab === 'products' ? [
            {
              name: 'name',
              label: 'Product Name',
              type: 'text',
              placeholder: 'Enter product name',
              required: true
            },
            {
              name: 'version',
              label: 'Version',
              type: 'text',
              placeholder: 'e.g., 1.0.0',
              required: false
            }
          ] : []
        }
      />

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          options={getContextMenuOptions()}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Delete Product Modal */}
      <DeleteProductModal
        isOpen={showDeleteProductModal}
        onClose={() => {
          setShowDeleteProductModal(false);
          setProductToDelete(null);
        }}
        onConfirm={confirmProductDelete}
        product={productToDelete}
      />

      {/* Delete Confirmation Modal for Configurations */}
      <DeleteConfirmationModal {...configDeleteModal} />
    </div>
  );
}
